import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { CLINIC, SERVICES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", phone: "", email: "", service: "", preferred_time: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill in your name, phone, email and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true);
      setForm(empty);
      toast.success("Request received! We'll call you shortly to confirm.");
    } catch (err) {
      toast.error("Something went wrong. Please call us on " + CLINIC.phoneDisplay);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-pd-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Info */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs uppercase tracking-[0.25em] font-medium text-pd-sage">Get In Touch</span>
          <h2 className="mt-4 font-display text-4xl lg:text-5xl font-medium leading-tight text-pd-forest">
            Request an <span className="italic text-pd-accent">appointment.</span>
          </h2>
          <p className="mt-5 text-lg text-pd-body max-w-md">
            Leave your details and our friendly reception team will call you back to find a
            time that suits you. For emergencies, call us directly.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${CLINIC.phoneRaw}`} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-2xl bg-pd-bg flex items-center justify-center"><Phone size={20} className="text-pd-accent" /></span>
              <div>
                <div className="text-xs uppercase tracking-wider text-pd-muted">Call us</div>
                <div className="text-pd-forest font-medium group-hover:text-pd-accent transition-colors">{CLINIC.phoneDisplay}</div>
              </div>
            </a>
            <a href={`mailto:${CLINIC.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-2xl bg-pd-bg flex items-center justify-center"><Mail size={20} className="text-pd-accent" /></span>
              <div>
                <div className="text-xs uppercase tracking-wider text-pd-muted">Email</div>
                <div className="text-pd-forest font-medium group-hover:text-pd-accent transition-colors">{CLINIC.email}</div>
              </div>
            </a>
            <a href={CLINIC.mapUrl} target="_blank" rel="noreferrer" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-2xl bg-pd-bg flex items-center justify-center"><MapPin size={20} className="text-pd-accent" /></span>
              <div>
                <div className="text-xs uppercase tracking-wider text-pd-muted">Visit us</div>
                <div className="text-pd-forest font-medium group-hover:text-pd-accent transition-colors">{CLINIC.address}</div>
              </div>
            </a>
          </div>

          <div className="mt-10 bg-pd-bg rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={18} className="text-pd-accent" />
              <span className="font-display text-xl text-pd-forest">Opening Hours</span>
            </div>
            <div className="space-y-2">
              {CLINIC.hours.map(([d, h]) => (
                <div key={d} className="flex justify-between text-sm">
                  <span className="text-pd-muted">{d}</span>
                  <span className="text-pd-forest font-medium">{h}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-pd-bg rounded-[2rem] p-8 md:p-10 shadow-xl shadow-pd-forest/5"
        >
          {done ? (
            <div data-testid="form-success" className="h-full min-h-[420px] flex flex-col items-center justify-center text-center">
              <CheckCircle2 size={56} className="text-pd-sage" />
              <h3 className="mt-5 font-display text-3xl text-pd-forest">Thank you!</h3>
              <p className="mt-3 text-pd-body max-w-xs">Your request has been received. We'll be in touch very soon to confirm your appointment.</p>
              <button onClick={() => setDone(false)} data-testid="form-reset-btn" className="mt-6 text-pd-accent font-medium hover:underline">
                Submit another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="appointment-form" className="space-y-5">
              <Field label="Full Name *">
                <input data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Jane Smith" className={inputCls} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Phone *">
                  <input data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="0400 000 000" className={inputCls} />
                </Field>
                <Field label="Email *">
                  <input data-testid="input-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className={inputCls} />
                </Field>
              </div>
              <Field label="Service of Interest *">
                <select data-testid="input-service" value={form.service} onChange={update("service")} className={inputCls}>
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s.title} value={s.title}>{s.title}</option>
                  ))}
                  <option value="General Check-up">General Check-up & Clean</option>
                  <option value="Free Kids Dental">Free Kids Dental (CDBS)</option>
                  <option value="Emergency">Dental Emergency</option>
                </select>
              </Field>
              <Field label="Preferred Day / Time">
                <input data-testid="input-time" value={form.preferred_time} onChange={update("preferred_time")} placeholder="e.g. Weekday mornings" className={inputCls} />
              </Field>
              <Field label="Message (optional)">
                <textarea data-testid="input-message" value={form.message} onChange={update("message")} rows={3} placeholder="Tell us anything we should know…" className={inputCls + " resize-none"} />
              </Field>
              <button
                type="submit"
                data-testid="submit-appointment-btn"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-pd-terra hover:bg-pd-terradark disabled:opacity-70 text-white rounded-full px-8 py-4 font-medium transition-all duration-300 hover:shadow-lg hover:shadow-pd-terra/30"
              >
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Request Appointment"}
              </button>
              <p className="text-xs text-center text-pd-muted">We'll never share your details. By submitting you agree to be contacted about your enquiry.</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-pd-surface/60 border border-transparent focus:border-pd-sage focus:bg-white rounded-xl px-4 py-3 text-pd-forest placeholder:text-pd-muted/70 outline-none transition-all duration-200";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-pd-forest mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
